import { DependencyList, useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, fetchBlob } from 'api/client'

import { useToast } from 'components/Toast'
import { t } from 'lib/i18n'

export const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : t('حدث خطأ غير متوقع', 'Something went wrong')

export const useAsync = <T>(loader: () => Promise<T>, deps: DependencyList) => {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const run = useRef(0)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(loader, deps)

  const reload = useCallback(async () => {
    const current = ++run.current
    setLoading(true)
    setError(null)
    try {
      const result = await load()
      if (current === run.current) setData(result)
    } catch (caught) {
      if (current === run.current) setError(errorMessage(caught))
    } finally {
      if (current === run.current) setLoading(false)
    }
  }, [load])

  useEffect(() => {
    reload()
  }, [reload])

  return { data, error, loading, reload, setData }
}

/** Runs an action with busy state and toast feedback; returns the result or undefined on failure. */
export const useAction = () => {
  const notify = useToast()
  const [busy, setBusy] = useState<string | null>(null)

  const run = useCallback(
    async <T>(key: string, action: () => Promise<T>, success?: string): Promise<T | undefined> => {
      setBusy(key)
      try {
        const result = await action()
        if (success) notify(success)
        return result
      } catch (caught) {
        notify(errorMessage(caught), 'error', caught instanceof ApiError ? caught.details : undefined)
        return undefined
      } finally {
        setBusy(null)
      }
    },
    [notify]
  )

  return { busy, run }
}

/** Tracks browser connectivity via the online/offline events, for a persistent "you're offline" banner. */
export const useOnlineStatus = () => {
  const [online, setOnline] = useState(() => navigator.onLine)

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  return online
}

export const useObjectUrl = (path: string | null) => {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!path) return undefined
    let objectUrl: string | null = null
    let cancelled = false
    fetchBlob(path)
      .then(({ blob }) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => setUrl(null))
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path])

  return url
}
